import React, { useEffect, useState } from 'react';
import { CornerDownRight, Loader2, Send, Trash2 } from 'lucide-react';
import { listCourseDiscussions, postDiscussion, deleteDiscussion, type DiscussionPost } from '../../lib/discussions';
import { Stamp } from '../lms/ui';
import { Skeleton } from '../lms/Skeletons';

interface DiscussionPanelProps {
  courseId: string;
  lessonId?: string;
  lessonTitle?: string;
  userId: string;
  isAdmin: boolean;
}

const initial = (name?: string) => (name || '?').slice(0, 1).toUpperCase();

const DiscussionPanel: React.FC<DiscussionPanelProps> = ({ courseId, lessonId, lessonTitle, userId, isAdmin }) => {
  const threadLessonId = lessonId && !lessonId.startsWith('local-') ? lessonId : undefined;
  const [posts, setPosts] = useState<DiscussionPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setPosts(await listCourseDiscussions(courseId, threadLessonId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, threadLessonId]);

  const submit = async (text: string, parentId?: string | null) => {
    if (!text.trim()) return;
    setBusy(parentId || 'root');
    setError('');
    try {
      await postDiscussion({ userId, courseId, lessonId: threadLessonId, parentId, body: text });
      if (parentId) {
        setReplyTo(null);
        setReplyBody('');
      } else {
        setBody('');
      }
      await load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not post.';
      setError(message.includes('does not exist') ? 'Discussions are not enabled yet — run the experience migration in Supabase.' : message);
    } finally {
      setBusy('');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this post?')) return;
    try {
      await deleteDiscussion(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete.');
    }
  };

  const renderPost = (p: DiscussionPost, isReply = false) => (
    <div key={p.id} className={isReply ? 'ml-5 mt-3 border-l border-rule pl-4' : 'py-4'}>
      <div className="flex items-start gap-3">
        <div className={`flex h-7 w-7 flex-none items-center justify-center font-mono text-[10px] font-semibold ${p.author_role === 'ADMIN' ? 'bg-pine text-paper' : 'bg-paper-2 text-ink-2'}`}>
          {initial(p.author_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px] font-semibold text-ink">{p.author_name || 'Student'}</span>
            {p.author_role === 'ADMIN' && <Stamp tone="issued">Instructor</Stamp>}
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
              {new Date(p.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-[13px] leading-5 text-ink">{p.body}</p>
          <div className="mt-1.5 flex items-center gap-4">
            {!isReply && (
              <button onClick={() => { setReplyTo(replyTo === p.id ? null : p.id); setReplyBody(''); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-pine hover:underline">
                <CornerDownRight className="h-3 w-3" strokeWidth={1.5} /> Reply
              </button>
            )}
            {(p.user_id === userId || isAdmin) && (
              <button onClick={() => void remove(p.id)} className="inline-flex items-center gap-1 text-[11px] text-ink-2 hover:text-danger">
                <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Delete
              </button>
            )}
          </div>

          {replyTo === p.id && (
            <div className="mt-2 flex gap-2">
              <input
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Write a reply…"
                className="lms-field flex-1"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(replyBody, p.id); } }}
              />
              <button
                onClick={() => void submit(replyBody, p.id)}
                disabled={busy === p.id || !replyBody.trim()}
                className="lms-btn-primary !px-3"
                aria-label="Send reply"
              >
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
              </button>
            </div>
          )}

          {p.replies?.map((r) => renderPost(r, true))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-ink">Questions &amp; answers</h2>
          <p className="mt-0.5 text-[12px] text-ink-2">
            {threadLessonId ? `Ask about “${lessonTitle}”.` : 'Ask anything about the course — instructors and fellow students can reply.'}
          </p>
        </div>
        <Stamp tone="neutral">{threadLessonId ? 'This lesson' : 'Whole course'}</Stamp>
      </div>

      {error && <div className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger">{error}</div>}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder={lessonTitle ? 'Type your question…' : 'Start a course discussion…'}
          className="lms-field flex-1 resize-y"
        />
        <button
          onClick={() => void submit(body)}
          disabled={busy === 'root' || !body.trim()}
          className="lms-btn-primary self-end sm:self-stretch"
        >
          {busy === 'root' ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
          Post
        </button>
      </div>

      {loading ? (
        <div className="mt-5 space-y-4 border-t border-rule pt-4" role="status" aria-busy="true" aria-label="Loading discussion">
          <span className="sr-only">Loading discussion…</span>
          {[0, 1].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-8 w-8 flex-none rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-3.5 w-32 rounded-md" />
                <Skeleton className="h-3 w-full rounded-md" />
                <Skeleton className="h-3 w-4/5 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <p className="mt-5 border-t border-rule pt-4 text-[12.5px] text-ink-2">No questions yet. Be the first to ask.</p>
      ) : (
        <div className="mt-2 divide-y divide-rule border-t border-rule">{posts.map((p) => renderPost(p))}</div>
      )}
    </div>
  );
};

export default DiscussionPanel;
